<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="flex flex-col gap-3 mb-6 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 class="text-2xl font-bold text-white sm:text-3xl">Administrar Noticias</h1>
          <p class="text-sm text-gray-400 mt-1">
            Crear, editar y publicar noticias
          </p>
        </div>
        <div class="flex w-full gap-2 sm:w-auto">
          <router-link
            v-if="canManageCategories"
            to="/admin/noticias/categorias"
            class="btn-bochas-trans flex-1 whitespace-nowrap text-center text-sm sm:flex-none"
          >
            Categorías
          </router-link>
          <router-link to="/admin/noticias/nueva" class="btn-bochas-rojo flex-1 whitespace-nowrap text-center text-sm sm:flex-none">
            + Nueva noticia
          </router-link>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-16">
        <div class="inline-block w-12 h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin">
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 rounded-lg p-4 mb-6">
        {{ error }}
      </div>

      <!-- Empty -->
      <div v-else-if="noticias.length === 0" class="card-fbochas p-12 text-center">
        <p class="text-gray-400 mb-4">Todavía no hay noticias.</p>
        <router-link to="/admin/noticias/nueva" class="btn-bochas-rojo inline-block">
          Crear la primera
        </router-link>
      </div>

      <!-- Tabla desktop / Cards mobile -->
      <div v-else>
        <div class="hidden md:block card-fbochas overflow-hidden">
          <table class="w-full">
          <thead class="bg-white/5">
            <tr class="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
              <th class="px-6 py-3">Título</th>
              <th class="px-6 py-3">Estado</th>
              <th class="px-6 py-3">Fecha</th>
              <th v-if="canViewRecordHistory" class="px-6 py-3">Registro</th>
              <th class="px-6 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-white/5">
            <tr v-for="n in paginatedNoticias" :key="n.id" class="hover:bg-white/5 transition-colors">
              <td class="px-6 py-4">
                <div class="text-white font-medium">{{ n.title }}</div>
                <div v-if="n.tag" class="text-xs text-gray-500 mt-1">
                  {{ n.tag.name }}
                </div>
              </td>
              <td class="px-6 py-4">
                <span v-if="n.status === 'published'"
                  class="inline-block px-2 py-1 text-xs font-semibold rounded bg-green-500/20 text-green-300">
                  Publicada
                </span>
                <span v-else
                  class="inline-block px-2 py-1 text-xs font-semibold rounded bg-yellow-500/20 text-yellow-300">
                  Borrador
                </span>
              </td>
              <td class="px-6 py-4 text-sm text-gray-400">
                {{ formatDate(n.published_at || n.createdAt) }}
              </td>
              <td v-if="canViewRecordHistory" class="px-6 py-4"><AdminRecordDates v-if="canViewRecordHistory" resource="news" :record="n" /></td>
              <td class="px-6 py-4 text-right">
                <div class="inline-flex items-center justify-end gap-2">
                  <router-link
                    v-if="n.status === 'published'"
                      :to="{ path: `/noticias/${n.id}`, query: { fromAdmin: 'noticias' } }"
                    class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-white/80 text-bochas-azul hover:bg-white/10"
                    title="Abrir"
                    aria-label="Abrir noticia"
                  ><IconFbochas name="arrow-up-right" :size="16" /></router-link>
                  <router-link
                    :to="`/admin/noticias/editar/${n.id}`"
                    class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                    title="Editar"
                    aria-label="Editar noticia"
                  ><IconFbochas name="edit" :size="16" /></router-link>
                  <button
                    @click="onDelete(n)"
                    class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                    title="Eliminar"
                    aria-label="Eliminar noticia"
                  ><IconFbochas name="trash" :size="16" /></button>
                </div>
              </td>
            </tr>
          </tbody>
          </table>
        </div>

        <div class="md:hidden space-y-3">
          <div v-for="n in paginatedNoticias" :key="n.id" class="card-fbochas p-4">
            <div class="flex items-start justify-between gap-2 mb-2">
              <div class="min-w-0 flex-1">
                <h3 class="line-clamp-2 text-sm font-semibold leading-snug text-white">
                  {{ n.title }}
                </h3>
                <p v-if="n.tag" class="mt-1 truncate text-xs text-gray-500">
                  {{ n.tag.name }}
                </p>
              </div>
              <span
                v-if="n.status === 'published'"
                class="flex-shrink-0 rounded bg-green-500/20 px-2 py-1 text-xs font-semibold text-green-300"
              >
                Pub.
              </span>
              <span
                v-else
                class="flex-shrink-0 rounded bg-yellow-500/20 px-2 py-1 text-xs font-semibold text-yellow-300"
              >
                Borr.
              </span>
            </div>
            <p class="mb-3 text-xs text-gray-400">
              {{ formatDate(n.published_at || n.createdAt) }}
            </p>
            <AdminRecordDates v-if="canViewRecordHistory" resource="news" :record="n" class="mb-3" />
            <div class="flex items-center justify-end gap-2">
              <router-link
                v-if="n.status === 'published'"
                :to="{ path: `/noticias/${n.id}`, query: { fromAdmin: 'noticias' } }"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-white/80 text-bochas-azul hover:bg-white/10"
               title="Abrir" aria-label="Abrir"><IconFbochas name="arrow-up-right" :size="16" /></router-link>
              <span v-else class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 text-gray-500" title="Disponible al publicar" aria-label="Abrir: disponible al publicar" aria-disabled="true"><IconFbochas name="arrow-up-right" :size="16" /></span>
              <router-link
                :to="`/admin/noticias/editar/${n.id}`"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
               title="Editar" aria-label="Editar"><IconFbochas name="edit" :size="16" /></router-link>
              <button
                @click="onDelete(n)"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
               title="Eliminar" aria-label="Eliminar"><IconFbochas name="trash" :size="16" /></button>
            </div>
          </div>
        </div>

        <Paginacion
          :current-page="page"
          :total-items="noticias.length"
          :per-page="pageSize"
          :scroll-to-top="false"
          @change="page = $event"
        />
      </div>
    </div>
  </div>
</template>

<script setup>


import { computed, ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import AdminRecordDates from "@/components/Admin/AdminRecordDates.vue";
import Paginacion from "@/components/Common/Paginacion.vue";
import { listarNoticiasAdmin, eliminarNoticia } from "@/services/noticias/noticiasService";
import { formatDate } from "@/utils/date";
import { useConfirm } from "@/composables/useConfirm";
import { getStoredUser } from "@/services/auth/authService";
import { isGeneralAdmin } from "@/utils/roles";

const canViewRecordHistory = computed(() => isGeneralAdmin(getStoredUser()));

const route = useRoute();
const { confirm } = useConfirm();
const canManageCategories = isGeneralAdmin(getStoredUser());

// Tipo de contenido que lista está pantalla (definido en router/index.js meta.contentType)
const contentType = route.meta?.contentType || "NOTICIA";

const noticias = ref([]);
const loading = ref(true);
const error = ref("");
const page = ref(1);
const pageSize = 10;
const paginatedNoticias = computed(() =>
  noticias.value.slice((page.value - 1) * pageSize, page.value * pageSize),
);

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const data = await listarNoticiasAdmin({ type: contentType });
    // Filtrar por el type de la ruta
    noticias.value = (data.items || []).filter((n) => n.type === contentType);
    const totalPages = Math.max(1, Math.ceil(noticias.value.length / pageSize));
    page.value = Math.min(page.value, totalPages);
  } catch (err) {
    error.value = "Error al cargar.";
  } finally {
    loading.value = false;
  }
}

async function onDelete(n) {
  const confirmed = await confirm({
    title: "Eliminar",
    message: `¿Eliminar "${n.title}"?`,
  });
  if (!confirmed) return;
  try {
    await eliminarNoticia(n.id);
    await load();
  } catch (err) {
    error.value = "No se pudo eliminar.";
  }
}

onMounted(load);
</script>
